import { useEffect, useRef, useState } from "react";
import type { ApiClient } from "../../../api/api-client";
import { healthSchema, mintFeeSchema, rewardPolicySchema, rewardPoolSchema, randomnessHealthSchema,
  type BootstrapResponse, type NetBuyResponse } from "../../../api/schemas";
import type { MiningSnapshot } from "../../../mining-engine";
import type { WalletSessionSnapshot } from "../../../wallet/wallet-session";
import { errorMessage } from "../../../state/model-helpers";
import { useNetBuyLive } from "./use-net-buy-live";
import type { NetBuyUpdatesView } from "../types";
import { ELYSIUM, elysiumBootstrapReasons } from "../../../config/elysium";
import { useRewardPoolLive } from "./use-reward-pool-live";

type ReadData = {
  netBuy: NetBuyResponse | null;
  pool: ReturnType<typeof rewardPoolSchema.parse> | null;
  fee: ReturnType<typeof mintFeeSchema.parse> | null;
  policy: ReturnType<typeof rewardPolicySchema.parse> | null;
  health: ReturnType<typeof healthSchema.parse> | null;
  randomness: ReturnType<typeof randomnessHealthSchema.parse> | null;
};
type ReadKey = Exclude<keyof ReadData, "netBuy">;
export type LiveReads = ReadData & {
  contextKey: string;
  netBuyUpdates?: NetBuyUpdatesView;
  netBuyDisplayData?: NetBuyResponse | null;
  poolDisplayData?: ReadData["pool"];
  status: Record<keyof ReadData, { loading: boolean; error: string | null }>;
};
const READ_KEYS: ReadKey[] = ["pool", "fee", "policy", "health", "randomness"];

function emptyReads(contextKey: string, loading = true, error: string | null = null): LiveReads { return {
  contextKey,
  netBuy: null,
  pool: null,
  fee: null,
  policy: null,
  health: null,
  randomness: null,
  status: Object.fromEntries(["netBuy", ...READ_KEYS].map((key) => [key, { loading, error }])) as LiveReads["status"],
}; }

interface Options {
  api: ApiClient;
  bootstrap: BootstrapResponse | null;
  bootstrapError: string | null;
  tokenAddress: string | undefined;
  walletSnapshot: WalletSessionSnapshot;
  miningSnapshot: MiningSnapshot;
  contextKey: string;
  readContextKey(): string;
  publicContextKey: string;
  readPublicContextKey(): string;
  refreshRevision: number;
  terminalActive?: boolean;
}

export function useDashboardReads({ api, bootstrap, bootstrapError, tokenAddress, walletSnapshot,
  miningSnapshot, contextKey, readContextKey, publicContextKey, readPublicContextKey, refreshRevision, terminalActive = true }: Options): LiveReads {
  const [storedReads, setReads] = useState<LiveReads>(() => emptyReads(""));
  const readGeneration = useRef(0);
  const ready = Boolean(bootstrap);
  const v3 = bootstrap?.sourceChainId === ELYSIUM.chainId;
  const financialIdentityValid = !v3 || Boolean(bootstrap && elysiumBootstrapReasons(bootstrap).length === 0);
  const legacyPoolActive = !v3 && terminalActive;
  const pool = useRewardPoolLive(api, ready && financialIdentityValid && terminalActive && v3 && tokenAddress?.toLowerCase() === ELYSIUM.token.toLowerCase(),
    publicContextKey, readPublicContextKey, refreshRevision);
  const account = walletSnapshot.account;
  const netBuy = useNetBuyLive({ api, enabled: ready && financialIdentityValid && terminalActive, chainId: bootstrap?.sourceChainId, tokenAddress, account,
    contextKey, readContextKey, refreshRevision });
  const mining = useRef(false);
  mining.current = miningSnapshot.phase === "RUNNING";
  const refreshReads = useRef<() => void>(() => {});
  const committedRefreshRevision = useRef(refreshRevision);
  const lastRefreshRevision = useRef(refreshRevision);
  useEffect(() => { committedRefreshRevision.current = refreshRevision; }, [refreshRevision]);
  useEffect(() => {
    readGeneration.current++;
    setReads(emptyReads(publicContextKey));
  }, [publicContextKey]);
  useEffect(() => {
    if (!ready) return;
    const abort = new AbortController();
    const generation = ++readGeneration.current;
    // This generation's initial reads already serve a simultaneous refresh.
    lastRefreshRevision.current = committedRefreshRevision.current;
    // These resources belong to the deployment/token, independently of SIWE.
    const requestContext = publicContextKey;
    const pending = new Set<ReadKey>();
    const queued = new Set<ReadKey>();
    const started = new Map<ReadKey, number>();
    const cadence = (key: ReadKey) => key === "pool" ? 15_000 : key === "policy" ? 300_000 :
      key === "health" || key === "randomness" ? 30_000 : mining.current ? 5_000 : 12_000;
    const isCurrent = () => !abort.signal.aborted && generation === readGeneration.current && requestContext === readPublicContextKey();
    const load = (force = false, keys = READ_KEYS) => {
      if (!isCurrent()) return;
      const token = tokenAddress ?? null;
      const tasks: { [K in ReadKey]: (() => Promise<ReadData[K]>) | null } = {
        fee: () => api.get("/mint/fee", mintFeeSchema, abort.signal),
        policy: () => api.get("/rewards/policy", rewardPolicySchema, abort.signal),
        health: () => api.get("/health", healthSchema, abort.signal),
        randomness: () => api.get("/health/randomness", randomnessHealthSchema, abort.signal),
        pool: token && legacyPoolActive ? () => api.get(`/tokens/${token}/reward-pool`, rewardPoolSchema, abort.signal) : null,
      };
      for (const key of keys) {
        if (pending.has(key)) { if (force) queued.add(key); continue; }
        const previous = started.get(key);
        if (!force && previous !== undefined && performance.now() - previous < cadence(key)) continue;
        const task = tasks[key];
        const publish = (value: ReadData[ReadKey], loading: boolean, error: string | null) => {
          if (!isCurrent()) return;
          setReads((current) => current.contextKey !== requestContext || !isCurrent() ? current : {
            ...current, [key]: value, status: { ...current.status, [key]: { loading, error } },
          });
        };
        started.set(key, performance.now());
        if (!task) { publish(null, false, null); continue; }
        pending.add(key);
        setReads((current) => current.contextKey !== requestContext || !isCurrent() ? current : {
          ...current, status: { ...current.status, [key]: { loading: true, error: null } },
        });
        // Each response updates only its own resource. A slow request must not
        // delay other results or overlap with itself on the next polling tick.
        (async () => {
          try { publish(await task(), false, null); }
          catch (error) { publish(null, false, errorMessage(error)); }
          finally {
            pending.delete(key);
            if (queued.delete(key) && isCurrent()) load(true, [key]);
          }
        })().catch(() => console.error("Dashboard read state could not be published."));
      }
    };
    const refresh = () => load(true);
    refreshReads.current = refresh;
    void load();
    const poll = () => { if (document.visibilityState !== "hidden") load(); };
    const timer = setInterval(poll, 1_000);
    document.addEventListener("visibilitychange", poll);
    return () => {
      abort.abort(); clearInterval(timer);
      document.removeEventListener("visibilitychange", poll);
      if (refreshReads.current === refresh) refreshReads.current = () => {};
    };
  }, [api, ready, tokenAddress, publicContextKey, readPublicContextKey, legacyPoolActive]);
  useEffect(() => {
    if (lastRefreshRevision.current !== refreshRevision) {
      lastRefreshRevision.current = refreshRevision;
      refreshReads.current();
    }
  }, [refreshRevision]);

  if (!bootstrap && bootstrapError) return emptyReads(contextKey, false, "BACKEND_UNAVAILABLE");
  const reads = storedReads.contextKey === publicContextKey ? storedReads : emptyReads(publicContextKey);
  return { ...reads, ...(v3 ? { pool: pool.data, poolDisplayData: pool.lastDisplayData } : {}), contextKey, netBuy: netBuy.data, netBuyDisplayData: netBuy.lastDisplayData, netBuyUpdates: netBuy.updates,
    status: { ...reads.status, ...(v3 ? { pool: { loading: pool.loading, error: pool.error } } : {}), netBuy: netBuy.status } };
}
